import { useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

export interface MonthYearPanelProps {
  /** Selected competence in `YYYY-MM` format, or null when nothing is picked. */
  value: string | null;
  /** Fires with the picked competence as `YYYY-MM`. */
  onSelect: (value: string) => void;
  /** When provided (and a value exists), renders a clear action below the grid. */
  onClear?: () => void;
  /** Label for the clear action. Defaults to `common:actions.clear`. */
  clearLabel?: string;
}

export function parseCompetence(value: string | null | undefined): {
  year: number;
  month: number;
} | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return null;
  return { year, month };
}

/**
 * MonthYearPanel — year navigation (← year →) + 12-month grid. Month names
 * come from `Intl.DateTimeFormat` with the active i18n locale (no hardcoded
 * arrays). Shared between `<MonthYearPicker>` (form input) and the DataTable
 * `month` filter type.
 */
export function MonthYearPanel({ value, onSelect, onClear, clearLabel }: MonthYearPanelProps) {
  const { t, i18n } = useTranslation('common');
  const parsed = parseCompetence(value);
  const [viewYear, setViewYear] = useState<number>(() => parsed?.year ?? new Date().getFullYear());

  const monthLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { month: 'short' });
    return Array.from({ length: 12 }, (_, index) =>
      formatter.format(new Date(2000, index, 1)).replace('.', ''),
    );
  }, [i18n.language]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  return (
    <div className="w-56" data-testid="month-year-panel">
      <div className="flex items-center justify-between pb-2">
        <button
          type="button"
          aria-label={t('month_picker.prev_year')}
          onClick={() => setViewYear((year) => year - 1)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-secondary-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-heading font-semibold text-secondary-950">{viewYear}</span>
        <button
          type="button"
          aria-label={t('month_picker.next_year')}
          onClick={() => setViewYear((year) => year + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-secondary-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1">
        {monthLabels.map((label, index) => {
          const month = index + 1;
          const isSelected = parsed?.year === viewYear && parsed.month === month;
          const isCurrent = viewYear === currentYear && month === currentMonth;
          return (
            <button
              key={month}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(`${viewYear}-${String(month).padStart(2, '0')}`)}
              className={cn(
                'h-9 rounded-md text-sm font-body capitalize transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                isSelected
                  ? 'bg-primary-500 font-medium text-white'
                  : isCurrent
                    ? 'font-medium text-primary-600 hover:bg-primary-50'
                    : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {onClear && parsed ? (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-body font-medium text-primary-500 transition-colors hover:text-primary-600"
          >
            {clearLabel ?? t('actions.clear')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
